import React, { useState } from 'react';
import Header from '../components/common/Header';
import Card from '../components/Card';
import './HowToPlay.css';

const Example = ({ title, description, cards, verdict, verdictType = 'valid' }) => {
  const [playing, setPlaying] = useState(false);
  const [runId, setRunId] = useState(0);
  const [done, setDone] = useState(false);

  const play = () => {
    setDone(false);
    setPlaying(false);
    setTimeout(() => {
      setRunId((id) => id + 1);
      setPlaying(true);
      setTimeout(() => setDone(true), cards.length * 240 + 450);
    }, 40);
  };

  return (
    <div className="htp-example">
      <h3 className="htp-example-title">{title}</h3>
      <p className="htp-example-desc">{description}</p>
      <div className="htp-card-row" key={runId}>
        {cards.map((c, i) => (
          <div key={i} className={`htp-card-slot ${playing ? 'htp-deal' : ''}`} style={{ animationDelay: `${i * 240}ms` }}>
            <Card card={c} playable={false} />
          </div>
        ))}
      </div>
      <div className={`htp-verdict ${done ? 'show' : ''} ${verdictType}`}>{verdict}</div>
      <button className="htp-play-btn" onClick={play}>▶ Play animation</button>
    </div>
  );
};

const HowToPlay = () => {
  return (
    <div className="htp-page">
      <Header />
      <div className="htp-container">
        <div className="htp-hero">
          <h1>How to Play Rummy</h1>
          <p>Learn the rules, card combinations, and how to make a valid hand — with quick animations.</p>
        </div>

        <section className="htp-section">
          <h2>🎯 Objective</h2>
          <p>Arrange all your cards into valid <strong>sequences</strong> and <strong>sets</strong> before your opponents, then declare. A valid hand needs at least <strong>one pure sequence</strong>.</p>
        </section>

        <section className="htp-section">
          <h2>🃏 Card Values</h2>
          <ul className="htp-list">
            <li><strong>Number cards (2–10)</strong> — worth their face value.</li>
            <li><strong>Face cards (J, Q, K)</strong> — worth 10 points each.</li>
            <li><strong>Ace (A)</strong> — worth 10 points (or 1 in a run like A-2-3).</li>
            <li><strong>Jokers</strong> — worth 0 points.</li>
          </ul>
        </section>

        <section className="htp-section">
          <h2>🟢 Pure Sequence</h2>
          <Example
            title="3+ cards · same suit · consecutive · no joker"
            description="The most important combination — you must have at least one pure sequence to declare."
            cards={[{ suit: 'hearts', value: '5' }, { suit: 'hearts', value: '6' }, { suit: 'hearts', value: '7' }]}
            verdict="✓ PURE SEQUENCE"
            verdictType="valid"
          />
        </section>

        <section className="htp-section">
          <h2>🟡 Impure Sequence</h2>
          <Example
            title="Same suit · consecutive · a JOKER fills the gap"
            description="A joker can stand in for a missing card. Here the Joker plays the role of the 7 of hearts."
            cards={[{ suit: 'hearts', value: '5' }, { suit: 'hearts', value: '6' }, { suit: 'joker', value: 'Joker' }]}
            verdict="✓ IMPURE SEQUENCE"
            verdictType="impure"
          />
        </section>

        <section className="htp-section">
          <h2>🔵 Set</h2>
          <Example
            title="3–4 cards · SAME rank · DIFFERENT suits"
            description="A set is three or four cards of the same value but different suits."
            cards={[{ suit: 'hearts', value: '7' }, { suit: 'spades', value: '7' }, { suit: 'diamonds', value: '7' }]}
            verdict="✓ SET"
            verdictType="valid"
          />
        </section>

        <section className="htp-section">
          <h2>⭐ Jokers</h2>
          <p>There are two kinds of jokers:</p>
          <ul className="htp-list">
            <li><strong>Printed joker</strong> — the joker card(s) in the deck.</li>
            <li><strong>Wild joker</strong> — one card is picked at random at the start; every card of that same rank also becomes a wild joker.</li>
          </ul>
          <div className="htp-card-row">
            <div className="htp-card-slot">
              <Card card={{ suit: 'joker', value: 'Joker' }} playable={false} />
            </div>
            <span className="htp-joker-note">← printed joker (0 points)</span>
          </div>
        </section>

        <section className="htp-section">
          <h2>✅ What makes a valid declaration?</h2>
          <ol className="htp-list">
            <li>At least <strong>1 pure sequence</strong> (no joker).</li>
            <li>At least <strong>1 more sequence</strong> (impure or pure) — so 2 sequences total.</li>
            <li><strong>Every card</strong> must be in a valid group — no leftover cards.</li>
            <li>Sets are optional but help you finish faster.</li>
          </ol>
        </section>

        <section className="htp-section">
          <h2>🔴 What is INVALID</h2>
          <Example
            title="Not consecutive"
            description="5♥ 6♥ 8♥ — there's a gap (the 7♥ is missing) and no joker to fill it, so this is not a valid sequence."
            cards={[{ suit: 'hearts', value: '5' }, { suit: 'hearts', value: '6' }, { suit: 'hearts', value: '8' }]}
            verdict="✗ INVALID"
            verdictType="invalid"
          />
        </section>

        <section className="htp-section">
          <h2>📊 Scoring &amp; Drops</h2>
          <ul className="htp-list">
            <li><strong>Deadwood</strong> — points of ungrouped cards (face cards = 10, numbers = face value, Ace = 10, joker = 0).</li>
            <li><strong>First drop</strong> (before your first turn) — 25 points (20 points in the 101 Pool game).</li>
            <li><strong>Middle drop</strong> — 40 points.</li>
            <li><strong>Full count</strong> (invalid declaration) — 80 points.</li>
          </ul>
        </section>

        <div className="htp-footer">
          <p>Good luck at the tables! 🍀</p>
        </div>
      </div>
    </div>
  );
};

export default HowToPlay;
